import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Paper, Body2, Button } from "@activecollab/components";
import PropTypes from "prop-types";
import { useLinkContext } from "~/components/LinkProvider/context";
import { Page } from "~/components/Page";
import AddPaymentMethodProcess from "~/components/Subscription/AddPaymentMethodProcess";
import SubscriptionHeader from "~/components/Subscription/SubscriptionHeader";
import SubscriptionInfoBlock from "~/components/Subscription/SubscriptionPlanDetails/SubscriptionInfoBlock";
import { showSuccessToast } from "~/components/Toast";
import SubscriptionExpiredCardInfoBlock from "~/pages/Subscription/SubscriptionExpiredCardInfoBlock";
import SuspensionInfoBlock from "~/pages/Subscription/SuspensionInfoBlock";
import * as actionTypes from "~/store/action_types";
import { fetchAccountSettings } from "~/store/actions/actions.accountSettings";
import { stopRetirement } from "~/store/actions/actions.cancelSubscription";
import {
  getAccountPlanInfo,
  getAccountStatus,
  getCurrentPlanName,
  getIsAccountActived,
  getIsAccountSuspended,
  getIsFailedPayment,
  getIsSuspendedTrial,
} from "~/store/selectors/selector.accountSettings";
import {
  getPaymentMethodCreditCard,
  isLegacyPaymentMethod,
  isPaymentMethodAvailable as getIsPaymentMethodAvailable,
  isPrePaidPaymentMethod as getIsPrePaidPaymentMethod,
} from "~/store/selectors/selector.billingPaymentMethod";

export const Layout = ({ pageTitle, headerTitle, children, renderFooter, backRoute, renderBackToProjects }) => {
  const dispatch = useDispatch();
  const accountPlanInfo = useSelector((state) => getAccountPlanInfo(state));
  const isPaymentMethodAvailable = useSelector(getIsPaymentMethodAvailable);
  const creditCard = useSelector(getPaymentMethodCreditCard);
  const currentPlan = useSelector((state) => getCurrentPlanName(state));
  const isSuspendedTrial = useSelector(getIsSuspendedTrial);
  const isFailedPayment = useSelector(getIsFailedPayment);
  const isSuspended = useSelector(getIsAccountSuspended);
  const isAccountActivated = useSelector(getIsAccountActived);
  const isPrePaidPaymentMethod = useSelector(getIsPrePaidPaymentMethod);
  const isFastSpring = useSelector(isLegacyPaymentMethod);
  const accountStatus = useSelector(getAccountStatus);
  const shouldShowCreditCardExpiredInfoBlock = useMemo(() => {
    return (
      accountStatus === "active" &&
      isPaymentMethodAvailable &&
      !isPrePaidPaymentMethod &&
      !isFastSpring &&
      creditCard.get("is_expired")
    );
  }, [accountStatus, creditCard, isFastSpring, isPaymentMethodAvailable, isPrePaidPaymentMethod]);
  const [openAddPaymentMethodProcessWizard, setOpenAddPaymentMethodProcessWizard] = useState(false);
  const footerLinks = [
    { url: "https://activecollab.com/help", label: "FAQ" },
    { url: "https://activecollab.com/privacy-policy", label: "Privacy Policy" },
    { url: "https://activecollab.com/terms", label: "Terms of Use" },
  ];

  const handleOnClick = useCallback(() => {
    dispatch(stopRetirement()).then((response) => {
      if (response?.type === actionTypes.STOP_RETIREMENT_SUCCESS) {
        showSuccessToast("Your subscription is active again");
        dispatch(fetchAccountSettings());
      }
    });
  }, [dispatch]);
  const { liveSupportQueryData } = useLinkContext();

  const handleOnAddCreditCardClick = useCallback(() => {
    setOpenAddPaymentMethodProcessWizard(true);
  }, []);

  const onCloseAddPaymentMethodProcessWizardClick = useCallback(() => {
    setOpenAddPaymentMethodProcessWizard(false);
  }, []);

  const handleOnSubmit = useCallback(() => {
    setOpenAddPaymentMethodProcessWizard(false);
  }, []);

  const queryParams = liveSupportQueryData ? `?${liveSupportQueryData}` : "";
  const helpUrl = `https://activecollab.com/help${queryParams}`;

  const statusToDisplay = useMemo(() => {
    if (accountPlanInfo.is_cancelled) return "cancelled";
    return "no_credit_card";
  }, [accountPlanInfo.is_cancelled]);

  const infoBlockElements = useMemo(() => {
    const elements = {};

    switch (statusToDisplay) {
      case "cancelled":
        elements.color = "red";
        elements.iconType = "warning";
        elements.pageTitle = "Subscription canceled";

        return elements;
      case "no_credit_card":
        elements.color = "orange";
        elements.iconType = "info";
        elements.pageTitle = "Add credit card";

        return elements;
    }
  }, [statusToDisplay]);

  const getInfoBlockContent = useCallback(
    (status) => {
      switch (status) {
        case "cancellation":
          return (
            <>
              <Body2 className="tw-mb-5">
                Your account will be retired in {accountPlanInfo.days_to_retirement} days.{" "}
                <a
                  href={helpUrl}
                  className="tw-no-underline tw-mr-1"
                  rel="noreferrer"
                  target="_blank"
                >
                  Contact our Support Team
                </a>
                if you need further assistance.
              </Body2>
              <Button onClick={handleOnClick}>Reactivate Account</Button>
            </>
          );
        case "no-credit-card":
          return (
            <>
              <Body2 className="tw-mb-5">
                In order to manage bundles and number of members, you can add a credit card to your account. We
                won&#39;t process any payments until you make your first purchase.
              </Body2>
              <Button onClick={handleOnAddCreditCardClick}>Add Credit Card</Button>
            </>
          );
        case "suspended-trial":
          return (
            <Body2>
              Thanks for giving ActiveCollab a go! If you need extra time before choosing a subscription, please
              <a
                href={helpUrl}
                target="_blank"
                rel="noreferrer"
                className="tw-ml-1 activity-link"
              >
                contact our support team.
              </a>
            </Body2>
          );
      }
    },
    [accountPlanInfo.days_to_retirement, handleOnAddCreditCardClick, handleOnClick, helpUrl]
  );

  return (
    <Page
      title={pageTitle}
      className="tw-w-full md:tw-w-[984px] md:tw-min-w-[644px] tw-m-auto tw-pt-0 tw-px-4 md:tw-px-8 tw-pb-8"
    >
      <SubscriptionHeader
        title={headerTitle}
        backRoute={backRoute}
        renderBackToProjects={renderBackToProjects}
      />
      <Page.Body>
        {accountPlanInfo.is_cancelled ? (
          <Paper
            type="paper-2"
            className="tw-mb-4"
          >
            <SubscriptionInfoBlock
              color={infoBlockElements.color}
              iconType={infoBlockElements.iconType}
              content={getInfoBlockContent("cancellation")}
              blockTitle={infoBlockElements.pageTitle}
            />
          </Paper>
        ) : null}
        {!isPaymentMethodAvailable && currentPlan?.toLowerCase().includes("app_sumo") ? (
          <Paper
            type="paper-2"
            className="tw-mb-4"
          >
            <SubscriptionInfoBlock
              color={infoBlockElements.color}
              iconType={infoBlockElements.iconType}
              content={getInfoBlockContent("no-credit-card")}
              blockTitle={infoBlockElements.pageTitle}
            />
          </Paper>
        ) : null}
        {isSuspendedTrial ? (
          <Paper
            type="paper-2"
            className="tw-mb-4"
          >
            <SubscriptionInfoBlock
              blockTitle="Your 14 day trial has expired"
              color="red"
              iconType="warning"
              content={getInfoBlockContent("suspended-trial")}
            />
          </Paper>
        ) : null}
        {isAccountActivated && (isFailedPayment || isSuspended) ? (
          <Paper
            type="paper-2"
            className="tw-mb-4"
          >
            <SubscriptionInfoBlock
              blockTitle={isSuspended ? "Account Suspended" : "Payment Failed "}
              color="red"
              iconType="warning"
              content={
                <SuspensionInfoBlock
                  isSuspended={isSuspended}
                  isFailedPayment={isFailedPayment}
                  isPrePaidPaymentMethod={isPrePaidPaymentMethod}
                  isFastSpring={isFastSpring}
                />
              }
            />
          </Paper>
        ) : null}
        {shouldShowCreditCardExpiredInfoBlock ? (
          <Paper
            type="paper-2"
            className="tw-mb-4"
          >
            <SubscriptionInfoBlock
              blockTitle="Credit Card Expired"
              color="red"
              iconType="warning"
              content={<SubscriptionExpiredCardInfoBlock />}
            />
          </Paper>
        ) : null}
        <Paper
          type="paper-2"
          className="tw-p-8"
        >
          {children}
        </Paper>
      </Page.Body>
      {renderFooter ? (
        <div className="tw-flex tw-gap-8 tw-mt-5 tw-pb-4">
          {footerLinks.map((link) => {
            return (
              <a
                href={`${link.url}`}
                target="_blank"
                rel="noreferrer"
                className="tw-no-underline hover:tw-underline"
                key={link.label}
              >
                <Body2 color="tertiary">{link.label}</Body2>
              </a>
            );
          })}
        </div>
      ) : null}
      <AddPaymentMethodProcess
        onClose={onCloseAddPaymentMethodProcessWizardClick}
        open={openAddPaymentMethodProcessWizard}
        onSubmit={handleOnSubmit}
      />
    </Page>
  );
};

Layout.propTypes = {
  pageTitle: PropTypes.string.isRequired,
  headerTitle: PropTypes.string.isRequired,
  backRoute: PropTypes.string,
  renderFooter: PropTypes.bool,
  children: PropTypes.any,
  renderBackToProjects: PropTypes.bool,
};

Layout.defaultProps = {
  renderBackToProjects: true,
};

export default Layout;
